import React, { Component } from "react";
import { connect } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import equipmentActions from "../../../../_actions/equipmentActions";
import equipClassOptions from "../../../../_helpers/equipClasses";
import ContractorSelector from "../_common/ContractorSelector";

class EquipmentUploadModal extends Component {
  state = {
    contractorId: 0,
    file: null,
    fileName: "",
    parsedData: [],
    parseErrors: [],
    isUploading: false,
    uploadError: null,
    uploadSuccess: false,
    successCount: 0,
  };

  requiredColumns = ["number", "class"];

  handleChange_file = (e) => {
    const file = e.target.files[0];
    if (file) {
      this.setState({ file, fileName: file.name, parseErrors: [], uploadError: null }, () => {
        this.parseCSV(file);
      });
    }
  };

  parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");

      if (lines.length < 2) {
        this.setState({
          parseErrors: ["CSV file must contain a header row and at least one data row."],
          parsedData: [],
        });
        return;
      }

      const headers = this.parseCSVLine(lines[0]).map((h) => h.toLowerCase().trim());
      const missingColumns = this.requiredColumns.filter(
        (col) => !headers.includes(col)
      );

      if (missingColumns.length > 0) {
        this.setState({
          parseErrors: [
            `Missing required columns: ${missingColumns.join(", ")}. ` +
              `Required columns are: ${this.requiredColumns.join(", ")}`,
          ],
          parsedData: [],
        });
        return;
      }

      const data = [];
      const errors = [];

      for (let i = 1; i < lines.length; i++) {
        const values = this.parseCSVLine(lines[i]);
        const row = {};

        headers.forEach((header, index) => {
          row[header] = values[index] || "";
        });

        // Validate required fields
        const rowErrors = [];
        if (!row.number) rowErrors.push("Number is required");
        if (!row.class) rowErrors.push("Class is required");

        // Validate class value — match on stored value OR the bracket code in the label
        if (row.class) {
          const inputLower = row.class.toLowerCase();
          const validClass = equipClassOptions.find((ec) => {
            if (ec.value.toLowerCase() === inputLower) return true;
            const bracketMatch = ec.label.match(/\[([^\]]+)\]/);
            return bracketMatch && bracketMatch[1].toLowerCase() === inputLower;
          });
          if (!validClass) {
            rowErrors.push(`Invalid class "${row.class}"`);
          } else {
            row.class = validClass.value; // Normalize to stored value
          }
        }

        if (rowErrors.length > 0) {
          errors.push(`Row ${i}: ${rowErrors.join(", ")}`);
        } else {
          data.push({
            number: row.number,
            equipClass: row.class,
            make: row.make || "",
            model: row.model || "",
            description: row.description || "",
            rate: parseFloat(row.rate) || 0,
            delayFactor: parseFloat(row.delayfactor) || 0,
            otFactor: parseFloat(row.otfactor) || 0,
            isRental: row.isrental?.toLowerCase() === "true" || row.isrental === "1",
          });
        }
      }

      this.setState({ parsedData: data, parseErrors: errors });
    };

    reader.readAsText(file);
  };

  parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote ("") inside a quoted field — emit one literal "
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { contractorId, parsedData } = this.state;
    const { projectId, dispatch, toggleUploadModal } = this.props;

    if (!contractorId || parsedData.length === 0) return;

    this.setState({ isUploading: true, uploadError: null });

    try {
      const equipmentList = parsedData.map((item) => ({
        ...item,
        contractorId,
        projectId,
      }));

      const result = await dispatch(
        equipmentActions.uploadCsv(projectId, equipmentList)
      );

      if (result && result.success !== false) {
        const successCount =
          result?.count ?? (Array.isArray(result) ? result.length : parsedData.length);
        this.setState({
          uploadSuccess: true,
          successCount,
          isUploading: false,
        });
        // Close modal after short delay to show success message
        setTimeout(() => {
          toggleUploadModal();
        }, 1500);
      } else {
        this.setState({
          uploadError: result?.message || "Upload failed. Please try again.",
          isUploading: false,
        });
      }
    } catch (err) {
      this.setState({
        uploadError: err.message || "An error occurred during upload.",
        isUploading: false,
      });
    }
  };

  clearFile = () => {
    this.setState({
      file: null,
      fileName: "",
      parsedData: [],
      parseErrors: [],
      uploadError: null,
    });
  };

  canSubmit = () => {
    const { contractorId, parsedData, parseErrors, isUploading } = this.state;
    return (
      contractorId > 0 &&
      parsedData.length > 0 &&
      parseErrors.length === 0 &&
      !isUploading
    );
  };

  render() {
    const {
      contractorId,
      fileName,
      parsedData,
      parseErrors,
      isUploading,
      uploadError,
      uploadSuccess,
      successCount,
    } = this.state;
    const { showUploadModal, toggleUploadModal, projectId } = this.props;

    const closeBtn = (
      <button className="close" onClick={toggleUploadModal}>
        <FontAwesomeIcon icon="times" className="fa-fw text-white" />
      </button>
    );

    return (
      <Modal
        isOpen={showUploadModal}
        toggle={toggleUploadModal}
        size="lg"
        fade={false}
      >
        <ModalHeader
          toggle={toggleUploadModal}
          className="bg-info text-white"
          close={closeBtn}
        >
          <FontAwesomeIcon icon="upload" className="fa-fw mr-2" />
          Upload Equipment from CSV
        </ModalHeader>
        <ModalBody>
          <form id="uploadEquipForm" onSubmit={this.handleSubmit}>
            <ContractorSelector
              projectId={projectId}
              contractorId={contractorId}
              setContractorId={(contractorId) => this.setState({ contractorId })}
            />

            <div className="card m-2">
              <div className="card-body">
                <div className="form-group mb-3">
                  <label htmlFor="csvFile" className="font-weight-bold">
                    Select CSV File:
                  </label>
                  <div className="custom-file">
                    <input
                      type="file"
                      className="custom-file-input"
                      id="csvFile"
                      accept=".csv"
                      onChange={this.handleChange_file}
                      disabled={!contractorId}
                    />
                    <label className="custom-file-label" htmlFor="csvFile">
                      {fileName || "Choose file..."}
                    </label>
                  </div>
                  {fileName && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mt-2"
                      onClick={this.clearFile}
                    >
                      <FontAwesomeIcon icon="times" className="mr-1" />
                      Clear
                    </button>
                  )}
                </div>

                <div className="alert alert-secondary mb-3">
                  <small>
                    <strong>Required CSV columns:</strong> Number, Class
                    <br />
                    <strong>Optional columns:</strong> Make, Model, Description, Rate,
                    DelayFactor, OTFactor, IsRental
                    <br />
                    <strong>Note:</strong> Class must match a valid equipment class code
                    (e.g., TRUCK, GRADR, TRACC)
                  </small>
                </div>

                {parseErrors.length > 0 && (
                  <div className="alert alert-danger">
                    <strong>Validation Errors:</strong>
                    <ul className="mb-0 mt-2">
                      {parseErrors.slice(0, 15).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                      {parseErrors.length > 15 && (
                        <li>...and {parseErrors.length - 15} more errors</li>
                      )}
                    </ul>
                  </div>
                )}

                {uploadError && (
                  <div className="alert alert-danger">
                    <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
                    {uploadError}
                  </div>
                )}

                {uploadSuccess && (
                  <div className="alert alert-success">
                    <FontAwesomeIcon icon="check-circle" className="mr-2" />
                    Successfully uploaded {successCount} equipment record(s).
                  </div>
                )}

                {parsedData.length > 0 && parseErrors.length === 0 && (
                  <div>
                    <h6>Preview ({parsedData.length} records):</h6>
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "300px", overflowY: "auto" }}
                    >
                      <table className="table table-sm table-bordered">
                        <thead className="thead-light">
                          <tr>
                            <th>Number</th>
                            <th>Class</th>
                            <th>Make</th>
                            <th>Model</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedData.slice(0, 20).map((row, index) => (
                            <tr key={index}>
                              <td>{row.number}</td>
                              <td>{row.equipClass}</td>
                              <td>{row.make}</td>
                              <td>{row.model}</td>
                              <td>{row.description}</td>
                            </tr>
                          ))}
                          {parsedData.length > 20 && (
                            <tr>
                              <td colSpan="5" className="text-center text-muted">
                                ...and {parsedData.length - 20} more records
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </form>
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <button
            type="submit"
            form="uploadEquipForm"
            className="btn btn-primary"
            disabled={!this.canSubmit()}
          >
            {isUploading ? (
              <>
                <FontAwesomeIcon icon="spinner" spin className="mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="upload" className="mr-2" />
                Upload {parsedData.length > 0 ? `(${parsedData.length})` : ""}
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary ml-2"
            onClick={toggleUploadModal}
          >
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  projContractorList:
    state.contractors.projContractorLists[ownProps.projectId] || null,
});

export default connect(mapStateToProps)(EquipmentUploadModal);